import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react';
import { MESSAGE_TYPES, TAB_TYPES } from '../constants';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';
import livekitService from '../services/livekitService';

// Tab types
const TAB_TYPES_EXTENDED = {
  ...TAB_TYPES,
  GENERAL: 'general',
  USER_SPEECH: 'user_speech',
  AI_SPEECH: 'ai_speech',
  SYSTEM: 'system'
};

// Initial state
const initialState = {
  messages: [],
  components: {}, // Store UI components with their metadata
  currentTab: TAB_TYPES_EXTENDED.GENERAL,
  userSpeechBuffer: '',
  aiSpeechBuffer: '',
  isConnected: false,
  connectionStatus: 'disconnected',
  error: null,
  messageCounter: 0, // Add counter for unique message IDs
  currentRoute: 'HOME' // Track current route for navigation
};

// Action types
const ACTIONS = {
  ADD_MESSAGE: 'ADD_MESSAGE',
  SET_CONNECTION_STATUS: 'SET_CONNECTION_STATUS',
  SET_ERROR: 'SET_ERROR',
  SET_CURRENT_TAB: 'SET_CURRENT_TAB',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
  UPDATE_USER_SPEECH_BUFFER: 'UPDATE_USER_SPEECH_BUFFER',
  UPDATE_AI_SPEECH_BUFFER: 'UPDATE_AI_SPEECH_BUFFER',
  FINALIZE_USER_SPEECH: 'FINALIZE_USER_SPEECH',
  FINALIZE_AI_SPEECH: 'FINALIZE_AI_SPEECH'
};

// Reducer
function chatReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_COMPONENTS':
      return {
        ...state,
        components: {
          ...state.components,
          ...action.payload
        }
      };
      
    case 'SET_CURRENT_ROUTE':
      return {
        ...state,
        currentRoute: action.payload
      };
      
    case ACTIONS.ADD_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, action.payload],
        messageCounter: state.messageCounter + 1
      };
    
    case ACTIONS.SET_CONNECTION_STATUS:
      return {
        ...state,
        isConnected: action.payload === 'connected',
        connectionStatus: action.payload
      };
    
    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload
      };
    
    case ACTIONS.SET_CURRENT_TAB:
      return {
        ...state,
        currentTab: action.payload
      };
    
    case ACTIONS.CLEAR_MESSAGES:
      return {
        ...state,
        messages: [],
        userSpeechBuffer: '',
        aiSpeechBuffer: '',
        messageCounter: 0
      };
    
    case ACTIONS.UPDATE_USER_SPEECH_BUFFER:
      return {
        ...state,
        userSpeechBuffer: state.userSpeechBuffer + action.payload
      };
    
    case ACTIONS.UPDATE_AI_SPEECH_BUFFER:
      return {
        ...state,
        aiSpeechBuffer: state.aiSpeechBuffer + action.payload
      };
    
    case ACTIONS.FINALIZE_USER_SPEECH:
      const finalUserText = action.payload || state.userSpeechBuffer;
      if (finalUserText.trim()) {
        const userMessage = {
          id: `user_${state.messageCounter}_${Date.now()}`,
          type: 'user_speech_chunk',
          content: finalUserText,
          timestamp: new Date().toISOString(),
          tab: 'user_speech'
        };
        return { 
          ...state, 
          messages: [...state.messages, userMessage], 
          userSpeechBuffer: '',
          messageCounter: state.messageCounter + 1
        };
      }
      return { ...state, userSpeechBuffer: '' };
    
    case ACTIONS.FINALIZE_AI_SPEECH:
      if (state.aiSpeechBuffer.trim()) {
        const aiMessage = {
          id: `ai_${state.messageCounter}_${Date.now()}`,
          type: MESSAGE_TYPES.AI_SPEECH_CHUNK,
          content: state.aiSpeechBuffer,
          timestamp: new Date().toISOString(),
          tab: TAB_TYPES.AI_SPEECH
        };
        return { 
          ...state, 
          messages: [...state.messages, aiMessage], 
          aiSpeechBuffer: '',
          messageCounter: state.messageCounter + 1
        };
      }
      return state;
    
    default:
      return state;
  }
}

// Create context
const ChatContext = createContext();

// Provider component
export function ChatProvider({ children }) {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const { voiceConnected, sessionId } = useAuth();
  const isDataChannelSetup = useRef(false);
  const componentRefs = useRef({});

  // Find a UI component by its ID
  const findComponent = useCallback((componentId) => {
    return state.components[componentId];
  }, [state.components]);

  // Handle click on a component
  const handleComponentClick = useCallback((componentId) => {
    const component = findComponent(componentId);
    if (!component) {
      console.warn(`Component not found: ${componentId}`);
      return false;
    }
    
    console.log(`🔘 Clicking component: ${componentId}`, component[0]); // component[0] is the display text
    
    // If the component has a ref, trigger a click on it
    const componentRef = componentRefs.current[componentId]?.current;
    if (componentRef) {
      try {
        componentRef.click();
        return true;
      } catch (error) {
        console.error(`Failed to click component ${componentId}:`, error);
      }
    }
    
    return false;
  }, [findComponent]);

  // Handle navigation to a new route
  const handleNavigation = useCallback((routeName) => {
    if (!routeName) {
      console.warn('No route name provided for navigation');
      return false;
    }
    
    console.log(`🛣️ Navigating to route: ${routeName}`);
    dispatch({ type: 'SET_CURRENT_ROUTE', payload: routeName });
    return true;
  }, []);

  // Send current runtime data to the server
  const sendCurrentRuntimeData = useCallback(() => {
    if (!sessionId) {
      console.warn('No session ID available to send runtime data');
      return;
    }
    
    try {
      // Convert component refs to a format the server expects
      const components = Object.entries(componentRefs.current).reduce((acc, [id, ref]) => {
        if (ref.current) {
          acc[id] = [
            ref.current.innerText || ref.current.value || '',
            ref.current.tagName.toLowerCase(),
            ...(ref.current.dataset.descriptions ? ref.current.dataset.descriptions.split(',') : [])
          ];
        }
        return acc;
      }, {});
      
      const runtimeData = {
        components,
        deviceId: 'browser',
        currentRoute: state.currentRoute
      };
      
      console.log('📤 Sending runtime data:', runtimeData);
      apiService.sendRuntimeData(sessionId, runtimeData);
    } catch (error) {
      console.error('Failed to send runtime data:', error);
    }
  }, [sessionId, state.currentRoute]);

  // Handle streaming messages from LiveKit
  const handleStreamingMessage = useCallback((data) => {
    console.log('📨 Handling message:', data);
    
    // Handle chat messages (both from user and AI)
    if (data.type === 'chat' || data.type === 'ai_response') {
      const isFromUser = data.sender === 'user';
      const messageContent = data.message || data.content || data.text || '';
      
      console.log(`💬 Processing ${isFromUser ? 'user' : 'AI'} message:`, messageContent);
      
      const message = {
        id: `msg_${Date.now()}_${isFromUser ? 'user' : 'ai'}`,
        type: isFromUser ? 'user_chat_message' : MESSAGE_TYPES.AI_SPEECH_CHUNK,
        content: messageContent,
        timestamp: data.timestamp ? new Date(data.timestamp).toISOString() : new Date().toISOString(),
        tab: TAB_TYPES_EXTENDED.CHAT,
        isFromUser
      };
      
      console.log('📝 Adding message to chat:', message);
      dispatch({ type: ACTIONS.ADD_MESSAGE, payload: message });
      return;
    }
    
    // Handle structured messages
    if (!data.data) {
      console.warn('Received message without data:', data);
      return;
    }
    
    switch (data.type) {
      case MESSAGE_TYPES.USER_SPEECH_CHUNK:
        if (data.data.is_final) {
          dispatch({ type: ACTIONS.FINALIZE_USER_SPEECH, payload: data.data.text_chunk });
        } else {
          dispatch({ type: ACTIONS.UPDATE_USER_SPEECH_BUFFER, payload: data.data.text_chunk });
        }
        break;
      
      case MESSAGE_TYPES.AI_SPEECH_CHUNK:
        if (data.data.is_final) {
          dispatch({ type: ACTIONS.FINALIZE_AI_SPEECH });
        } else {
          dispatch({ type: ACTIONS.UPDATE_AI_SPEECH_BUFFER, payload: data.data.text_chunk });
        }
        break;
      
      case MESSAGE_TYPES.AI_INTENT: {
        const intentData = data.data;
        let handled = false;
        
        // Handle different intent types
        if (intentData.actionType === 'click' && intentData.elementId) {
          handled = handleComponentClick(intentData.elementId);
        } else if (intentData.actionType === 'navigate' && intentData.routeName) {
          handled = handleNavigation(intentData.routeName);
        }
        
        // Log the intent and whether it was handled
        const intentMessage = { 
          id: `intent_${state.messageCounter}_${Date.now()}`,
          type: MESSAGE_TYPES.AI_INTENT,
          content: {
            ...intentData,
            handled,
            timestamp: new Date().toISOString()
          },
          timestamp: new Date().toISOString(),
tab: TAB_TYPES_EXTENDED.AI_INTENT
        };
        
        dispatch({ type: ACTIONS.ADD_MESSAGE, payload: intentMessage });
        break;
      }

      case MESSAGE_TYPES.AI_INTERRUPTED:
        sendCurrentRuntimeData();
        const interruptedMessage = { 
          id: `interrupted_${state.messageCounter}_${Date.now()}`, 
          type: MESSAGE_TYPES.AI_INTERRUPTED, 
          content: data.data.message, 
          timestamp: new Date().toISOString(), 
tab: TAB_TYPES_EXTENDED.SYSTEM 
        };
        dispatch({ type: ACTIONS.ADD_MESSAGE, payload: interruptedMessage });
        break;
      
      case MESSAGE_TYPES.REQUEST_RUNTIME_DATA:
        sendCurrentRuntimeData();
        break;
      
      case MESSAGE_TYPES.CONNECTION:
        dispatch({ type: ACTIONS.SET_CONNECTION_STATUS, payload: data.status });
        break;
      
      case MESSAGE_TYPES.KEEPALIVE:
        // This is just a heartbeat, no action needed.
        break;
      
      default:
        console.log('📡 Unknown message type:', data.type);
    }
  }, [handleComponentClick, handleNavigation, sendCurrentRuntimeData, state.messageCounter]);

  const setupLiveKitDataChannel = useCallback(() => {
    if (isDataChannelSetup.current) return;
    
    console.log('🔗 Setting up LiveKit data channel for chat...');
    
    // Set up data received handler
    const dataHandler = (data, participant) => {
      try {
        console.log('📨 Received message via LiveKit:', data);
        // Data is already parsed by livekitService
        handleStreamingMessage(data);
      } catch (error) {
        console.error('❌ Error handling LiveKit message:', error);
      }
    };
    
    livekitService.setOnDataReceived(dataHandler);
    
    // Cleanup function
    const cleanup = () => {
      livekitService.setOnDataReceived(null);
    };
    
    isDataChannelSetup.current = true;
    dispatch({ type: ACTIONS.SET_CONNECTION_STATUS, payload: 'connected' });
    console.log('✅ LiveKit data channel setup complete');
    
    return cleanup;
  }, [handleStreamingMessage]);

  // Register a component reference
  const registerComponent = useCallback((componentId, ref) => {
    if (componentId) {
      componentRefs.current[componentId] = ref;
    }
    return () => {
      if (componentId) {
        delete componentRefs.current[componentId];
      }
    };
  }, []);

  useEffect(() => {
    // Setup LiveKit data channel when voice is connected and we have a session
    if (voiceConnected && sessionId && !isDataChannelSetup.current) {
      setupLiveKitDataChannel();
    }

    // Cleanup when voice disconnects or session changes
    if (!voiceConnected && isDataChannelSetup.current) {
      console.log('🔌 Voice disconnected, cleaning up data channel...');
      isDataChannelSetup.current = false;
      dispatch({ type: ACTIONS.SET_CONNECTION_STATUS, payload: 'disconnected' });
    }
  }, [voiceConnected, sessionId, setupLiveKitDataChannel]);

  const changeTab = (tab) => {
    dispatch({ type: ACTIONS.SET_CURRENT_TAB, payload: tab });
  };

  const clearMessages = () => {
    dispatch({ type: ACTIONS.CLEAR_MESSAGES });
  };

  const getMessagesForCurrentTab = () => {
    if (state.currentTab === TAB_TYPES.GENERAL) {
      return state.messages;
    }
    return state.messages.filter(msg => msg.tab === state.currentTab);
  };

  const getTabCounts = () => {
    const counts = {};
    Object.values(TAB_TYPES).forEach(tab => {
      if (tab === TAB_TYPES.GENERAL) {
        counts[tab] = state.messages.length;
      } else {
        counts[tab] = state.messages.filter(msg => msg.tab === tab).length;
      }
    });
    return counts;
  };

  const sendChatMessage = async (messageText) => {
    try {
      if (!voiceConnected) {
        console.error('❌ Cannot send chat message - not connected to voice');
        return false;
      }
      
      // Create message object with consistent format
      const messageData = {
        type: 'chat',
        message: messageText,
        timestamp: Date.now(),
        sender: 'user'
      };
      
      console.log('💬 Sending chat message:', messageData);
      
      // Add user message to chat immediately for better UX
      const userMessage = {
        id: `user_chat_${state.messageCounter}_${Date.now()}`,
        type: 'user_chat_message',
        content: messageText,
        timestamp: new Date().toISOString(),
        tab: 'general',
        isFromUser: true
      };
      dispatch({ type: ACTIONS.ADD_MESSAGE, payload: userMessage });
      
      // Send the message to the server
      const success = await livekitService.sendData(messageData);
      
      if (!success) {
        console.error('❌ Failed to send message to server');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('❌ Failed to send chat message:', error);
      return false;
    }
  };

  // Update components when runtime data is received
  const updateComponents = (components) => {
    dispatch({ type: 'UPDATE_COMPONENTS', payload: components });
  };

  // Get current route
  const getCurrentRoute = () => {
    return state.currentRoute;
  };

  const value = {
    ...state,
    registerComponent,
    changeTab,
    clearMessages,
    getMessagesForCurrentTab,
    getTabCounts,
    sendChatMessage,
    updateComponents,
    getCurrentRoute,
    findComponent,
    handleComponentClick,
    handleNavigation,
    MESSAGE_TYPES,
    TAB_TYPES
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

// Hook to use chat context
export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}